import { User } from '@prisma/client'
import { Divider, Form, Radio, Switch, Typography } from 'antd'
import React from 'react'
import { CommonClient } from '~/agents/common/client'
import { AppSessionType } from '~/features/appSession/common'
import { ProviderAccountAuthorization } from '~/features/formConfiguration/client/components/CustomInputs/ProviderAccountAuthorization'
import { SelectProviderAccount } from '~/features/formConfiguration/client/components/CustomInputs/SelectProviderAccount'
import { Toolbox } from '~/plugins/toolbox/shared'
import { OnboardingValues } from '../../common'

type Props = {
  user: User
  appSession: AppSessionType
}

export const Settings: React.FC<Props> = ({ user, appSession }) => {
  const values = (appSession?.values?.onboarding ?? {}) as OnboardingValues

  const valuesSafe = {
    ...values,
  } as OnboardingValues

  const logic = CommonClient.Form.useLogic({
    appSession,
  })

  const hasPermissionCorrupted = (permissions: any[]) => {
    return permissions.some(permission => !!permission?.[0]?.isCorrupted)
  }

  const handleSubmit = async (valuesUpdated: OnboardingValues) => {
    const driveFilesBefore = values.rag?.googleDriveFiles ?? []
    const driveFilesAfter = valuesUpdated.rag?.googleDriveFiles ?? []

    const fileIdsBefore = driveFilesBefore.map(file => file.id)
    const fileIdsAfter = driveFilesAfter.map(file => file.id)

    const hasDriveFilesChanged = !Toolbox.UtilityItems.isEqual<string>(
      fileIdsBefore,
      fileIdsAfter,
    )

    if (hasDriveFilesChanged) {
      await logic.triggerHttpEvent('onboarding.rag.completed')
    }

    const permissionDriveManage = valuesUpdated.rag?.permissionManageFiles
    const permissionGmailRead = valuesUpdated.authorizations?.gmailReadEmails
    const permissionGmailDraft = valuesUpdated.authorizations?.gmailDraftEmails

    const isCorrupted = appSession?.statusOnboarding === 'CORRUPTED'
    const isStillCorrupted = hasPermissionCorrupted([
      permissionDriveManage,
      permissionGmailRead,
      permissionGmailDraft,
    ])

    if (isCorrupted && !isStillCorrupted) {
      await logic.submitOnboarding()
    }
  }

  return (
    <>
      <CommonClient.Form.Wrapper
        {...logic}
        onSubmit={handleSubmit}
        values={valuesSafe}
      >
        <Typography.Title level={4} style={{ margin: 0 }}>
          Inbox
        </Typography.Title>

        <Divider className="my-3" />

        <CommonClient.Form.InputWrapper
          label="Connect your inbox"
          description="Allow Cara to read & write emails"
        >
          <Form.Item name={['emailServices', 'isActive']}>
            <Switch size="small" />
          </Form.Item>
        </CommonClient.Form.InputWrapper>

        <Form.Item<OnboardingValues> shouldUpdate={true}>
          {instance => {
            const isActive = instance.getFieldValue([
              'emailServices',
              'isActive',
            ])

            if (!isActive) {
              return <></>
            }

            return (
              <Form.Item
                name={['emailServices', 'provider']}
                rules={[
                  {
                    required: true,
                    message: 'Choose a provider to continue',
                  },
                ]}
              >
                <Radio.Group
                  block={true}
                  optionType="button"
                  buttonStyle="solid"
                  options={[
                    {
                      label: 'Google',
                      value: 'gmail',
                    },
                    {
                      label: 'Outlook',
                      value: 'outlook',
                    },
                  ]}
                />
              </Form.Item>
            )
          }}
        </Form.Item>

        <Form.Item<OnboardingValues> shouldUpdate={true}>
          {instance => {
            const isActive = instance.getFieldValue([
              'emailServices',
              'isActive',
            ])

            const provider = instance.getFieldValue([
              'emailServices',
              'provider',
            ])

            const providerAccountId = instance.getFieldValue([
              'emailServices',
              'gmailProviderAccountId',
            ])

            if (!isActive) {
              return <></>
            }

            if (provider === 'outlook') {
              return (
                <Form.Item
                  name={['emailServices', 'outlookProviderAccountId']}
                  rules={[
                    {
                      required: true,
                      message: 'Connect your account to continue',
                    },
                  ]}
                >
                  <SelectProviderAccount providerKey="outlookMail" />
                </Form.Item>
              )
            }

            return (
              <>
                <Form.Item
                  name={['emailServices', 'gmailProviderAccountId']}
                  rules={[
                    {
                      required: true,
                      message: 'Connect your account to continue',
                    },
                  ]}
                >
                  <SelectProviderAccount providerKey="gmail" />
                </Form.Item>

                {providerAccountId && (
                  <>
                    <Form.Item
                      name={['authorizations', 'gmailReadEmails']}
                      rules={[
                        {
                          validator: (_, value) =>
                            value?.[0]?.isCorrupted
                              ? Promise.reject()
                              : Promise.resolve(),
                          message: 'Authorization is not valid',
                        },
                        {
                          required: true,
                          message: 'You need to authorize to continue',
                        },
                      ]}
                    >
                      <ProviderAccountAuthorization
                        providerAccountId={providerAccountId}
                        scope="https://www.googleapis.com/auth/gmail.modify"
                        ui={{
                          title: 'Read Emails',
                          iconAntd: 'GoogleOutlined',
                          description: 'Allow Cara to read emails',
                        }}
                      />
                    </Form.Item>
                    <Form.Item
                      name={['authorizations', 'gmailDraftEmails']}
                      rules={[
                        {
                          validator: (_, value) =>
                            value?.[0]?.isCorrupted
                              ? Promise.reject()
                              : Promise.resolve(),
                          message: 'Authorization is not valid',
                        },
                        {
                          required: true,
                          message: 'You need to authorize to continue',
                        },
                      ]}
                    >
                      <ProviderAccountAuthorization
                        providerAccountId={providerAccountId}
                        scope="https://www.googleapis.com/auth/gmail.send"
                        ui={{
                          title: 'Send Emails',
                          iconAntd: 'GoogleOutlined',
                          description: 'Allow Cara to send emails',
                        }}
                      />
                    </Form.Item>
                  </>
                )}
              </>
            )
          }}
        </Form.Item>
      </CommonClient.Form.Wrapper>
    </>
  )
}
